<template>
  <div class="container" v-loading="loading">
    <el-page-header :content="customer.name ? ('客户详情 - ' + customer.name) : '客户详情'" @back="$router.back()" class="mb-4" />

    <!-- 新出库未完结提醒：新出库 + status=4（未结清，待付款） -->
    <el-alert v-if="newoutPending.length" type="warning" show-icon :closable="false" class="mb-4 newout-pending-alert">
      <template slot="title">
        <span class="alert-title">该客户有 {{ newoutPending.length }} 笔「新出库」未完结（{{ newoutPendingLine }}）；如需修改状态请到下方「出库管理」模块</span>
      </template>
    </el-alert>

    <!-- 基础信息 -->
    <el-card class="mb-4">
      <div slot="header" class="card-head">
        <span><i class="el-icon-info"></i> 基础信息</span>
        <span>
          <el-button v-if="$hasPerm('func:customer:export')" type="success" size="small" icon="el-icon-download" :loading="exporting" @click="openExportDialog">导出</el-button>
          <el-button v-if="$hasPerm('func:customer:edit')" type="primary" size="small" icon="el-icon-edit" @click="openEdit">编辑</el-button>
        </span>
      </div>
      <el-descriptions :column="2" border size="small" :label-style="{width:'110px', fontSize:'15px'}" :content-style="{fontSize:'15px'}" class="base-info-desc">
        <el-descriptions-item label="客户ID">{{ customer.id }}</el-descriptions-item>
        <el-descriptions-item label="客户负责人">
          <span>{{ customer.responsiblePerson || '-' }}</span>
          <span v-if="customer.responsiblePerson" class="muted small" style="margin-left:6px;font-size:12px;color:#909399;">
            (出库权限：{{ managerOutboundTypes.length ? managerOutboundTypes.join('、') : '无' }})
          </span>
        </el-descriptions-item>
        <el-descriptions-item label="客户姓名">{{ customer.name || '-' }}</el-descriptions-item>
        <el-descriptions-item label="开发人">{{ customer.developer || '-' }}</el-descriptions-item>
        <el-descriptions-item label="性别">{{ customer.gender || '-' }}</el-descriptions-item>
        <el-descriptions-item label="无效客户">
          <el-tag :type="customer.invalidCustomer === 1 ? 'danger' : 'success'" size="mini">{{ customer.invalidCustomer === 1 ? '是' : '否' }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="年龄">{{ customer.age || '-' }}</el-descriptions-item>
        <el-descriptions-item label="客户类别">
          <el-tag v-if="customer.customerCategory" size="mini">{{ customer.customerCategory }}</el-tag>
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="手机">{{ maskPhone(customer.contactPhone) }}</el-descriptions-item>
        <el-descriptions-item label="座机">{{ maskPhone(customer.telephone) }}</el-descriptions-item>
        <el-descriptions-item label="客户标签">
          <el-tag :type="labelType(customer.customerLabel)" size="mini">{{ customer.customerLabel || '正常' }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="地址">{{ customer.address || '-' }}</el-descriptions-item>
        <el-descriptions-item label="创建日期">{{ formatDate(customer.createTime) }}</el-descriptions-item>
        <el-descriptions-item label="最新修改">{{ formatDate(customer.updateTime) }}</el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 客户实际存货 -->
    <el-card class="mb-4">
      <div slot="header" class="card-head" style="display:flex;justify-content:space-between;align-items:center;">
        <span><i class="el-icon-box"></i> 客户实际存货</span>
        <div>
          <el-radio-group v-model="stockTypeFilter" size="mini">
            <el-radio-button label="">全部</el-radio-button>
            <el-radio-button label="普通存货">普通存货</el-radio-button>
            <el-radio-button label="品鉴存货">品鉴存货</el-radio-button>
          </el-radio-group>
        </div>
      </div>
      <el-table :data="filteredStockRows" size="small" stripe border style="width:100%"
        :header-cell-style="{background:'#f5f7fa', color:'#606266'}">
        <el-table-column type="index" label="序号" width="60" align="center" :index="i => filteredStockRows.length - i"></el-table-column>
        <el-table-column prop="productName" label="产品名称" min-width="120" align="center"></el-table-column>
        <el-table-column prop="stockType" label="存货类型" width="120" align="center">
          <template slot-scope="s">
            <el-tag size="small" type="warning" style="font-weight:600;">{{ s.row.stockType || '普通存货' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="totalStocked" label="总存货数量" min-width="100" align="center"></el-table-column>
        <el-table-column label="已提数量" min-width="160" align="center">
          <template slot-scope="s">
            <div>{{ s.row.taken }}</div>
            <div v-if="Number(s.row.newoutTaken) > 0" style="font-size:13px;color:#E6A23C;font-weight:600;margin-top:2px;">含新出库已提 {{ s.row.newoutTaken }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="remaining" label="剩余数量" min-width="100" align="center">
          <template slot-scope="s">
            <span style="font-weight:600;color:#67C23A;">{{ s.row.remaining }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!filteredStockRows.length" class="muted" style="padding:12px;text-align:center;">暂无送存货记录</div>
    </el-card>

    <!-- 尊贵卡剩余可领取 -->
    <el-card class="mb-4" v-if="vipCards.length">
      <div slot="header" class="card-head">
        <span><i class="el-icon-bank-card"></i> 尊贵卡剩余可领取</span>
      </div>
      <el-table :data="vipCards" size="small" stripe border style="width:100%"
        :header-cell-style="{background:'#f5f7fa', color:'#606266'}">
        <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
        <el-table-column label="卡片编号" width="120" align="center">
          <template slot-scope="scope">
            <el-tag size="small" :type="vipTagType(scope.row.cardName)" :class="vipTagClass(scope.row.cardName)">{{ scope.row.label }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="产品/金额" min-width="160" align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.mode === 'amount'"><span style="color:#409EFF;font-weight:600;">金额</span></template>
            <template v-else>
              <div v-for="(p, i) in scope.row.products" :key="i" class="vip-row-cell">{{ p.productName }}</div>
              <span v-if="!scope.row.products || !scope.row.products.length" class="muted">-</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column label="剩余可领取" min-width="120" align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.mode === 'amount'">
              <span style="font-weight:600;color:#409EFF;">¥{{ formatMoney(scope.row.remainingAmount) }}</span>
            </template>
            <template v-else>
              <div v-for="(p, i) in scope.row.products" :key="i" class="vip-row-cell" style="font-weight:600;">{{ p.remaining }} 件</div>
              <span v-if="!scope.row.products || !scope.row.products.length" class="muted">-</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column label="应该剩余" min-width="120" align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.mode === 'amount'">
              <span style="font-weight:600;">¥{{ formatMoney(scope.row.shouldQuotaRemaining) }}</span>
            </template>
            <template v-else-if="scope.row.cardName === '尊贵卡C'">
              <div v-for="(p, i) in scope.row.products" :key="i" class="vip-row-cell" style="font-weight:600;">{{ p.shouldRemaining }} 件</div>
              <span v-if="!scope.row.products || !scope.row.products.length" class="muted">-</span>
            </template>
            <template v-else>
              <span class="muted">-</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column label="实际剩余" min-width="120" align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.mode === 'amount'">
              <span style="font-weight:600;color:#67C23A;">¥{{ formatMoney(scope.row.actualQuotaRemaining) }}</span>
            </template>
            <template v-else-if="scope.row.cardName === '尊贵卡C'">
              <div v-for="(p, i) in scope.row.products" :key="i" class="vip-row-cell" style="font-weight:600;color:#67C23A;">{{ p.actualRemaining }} 件</div>
              <span v-if="!scope.row.products || !scope.row.products.length" class="muted">-</span>
            </template>
            <template v-else>
              <span class="muted">-</span>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 销售订单 -->
    <el-card class="mb-4 embed-flat" v-if="customer.id">
      <div slot="header" class="card-head">
        <span><i class="el-icon-tickets"></i> 销售订单</span>
      </div>
      <sales-order :embedded-customer="customer"></sales-order>
    </el-card>

    <!-- 产品试用记录模块 -->
    <el-card class="mb-4" v-if="customer.id">
      <div slot="header" class="card-head">
        <span><i class="el-icon-s-opportunity"></i> 产品试用记录</span>
        <el-button type="success" size="mini" icon="el-icon-download" @click="exportTrialCsv" :disabled="!trialRecords.length">导出试用明细</el-button>
      </div>
      <el-table :data="trialRecords" size="small" stripe border style="width:100%"
        :header-cell-style="{background:'#f5f7fa', color:'#606266'}">
        <el-table-column type="index" label="序号" width="60" align="center" :index="i => trialRecords.length - i"></el-table-column>
        <el-table-column prop="productName" label="产品" min-width="200" align="center">
          <template slot-scope="s">
            <div style="font-weight:600;font-size:13px;">{{ s.row.productName || '-' }}</div>
            <div style="font-size:12px;color:#606266;margin-top:2px;">
              共 <strong style="color:#303133;">{{ s.row.totalQuantity !== undefined ? s.row.totalQuantity : (s.row.quantity || 0) }}</strong> 件
              <span style="font-size:11px;color:#909399;margin-left:2px;">(已审 {{ s.row.approvedQuantity !== undefined ? s.row.approvedQuantity : s.row.quantity }} · 待审 {{ s.row.pendingQuantity || 0 }})</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="试用类型" min-width="110" align="center">
          <template slot-scope="s">
            <el-tag v-if="s.row.applicableTypeName" :color="s.row.applicableTypeColor || ''" :style="s.row.applicableTypeColor ? 'color:#fff;border-color:' + s.row.applicableTypeColor : ''" size="mini">
              {{ s.row.applicableTypeName }}
            </el-tag>
            <span v-else style="color:#909399">-</span>
          </template>
        </el-table-column>
        <el-table-column label="试用次数" min-width="160" align="center">
          <template slot-scope="s">
            <div>
              <span style="color:#409EFF;font-weight:600">已申请 {{ s.row.totalApplyCount !== undefined ? s.row.totalApplyCount : ((s.row.approvedCount || 0) + (s.row.pendingCount || 0)) }} 次</span>
              <span v-if="s.row.applicableCount" style="color:#909399;font-size:12px;margin-left:4px">/ 限{{ s.row.maxAllowed || s.row.applicableCount }}次</span>
            </div>
            <div style="font-size:12px;color:#606266;margin-top:2px;">
              <span>已审核 <strong style="color:#67C23A;">{{ s.row.approvedCount || 0 }} 次</strong></span>
              <span style="margin: 0 4px; color: #dcdfe6;">|</span>
              <span>待审核 <strong :style="(s.row.pendingCount || 0) > 0 ? 'color:#E6A23C;' : 'color:#909399;'">{{ s.row.pendingCount || 0 }} 次</strong></span>
            </div>
            <div v-if="s.row.limitExceeded && s.row.isUnlocked" style="display:inline-flex;align-items:center;color:#67C23A;background:#f0f9eb;border:1px solid #c2e7b0;border-radius:4px;padding:3px 8px;font-size:12px;font-weight:600;margin-top:4px;">
              <i class="el-icon-unlock" style="margin-right:3px;font-weight:bold;"></i>已解除限制(可再申请1次)
            </div>
            <div v-else-if="s.row.limitExceeded" style="display:inline-flex;align-items:center;color:#F56C6C;background:#fef0f0;border:1px solid #fde2e2;border-radius:4px;padding:3px 8px;font-size:12px;font-weight:600;margin-top:4px;">
              <i class="el-icon-warning-outline" style="margin-right:3px;"></i>已达试用上限
            </div>
            <div v-else-if="s.row.isUnlocked" style="display:inline-flex;align-items:center;color:#67C23A;background:#f0f9eb;border:1px solid #c2e7b0;border-radius:4px;padding:3px 8px;font-size:12px;font-weight:600;margin-top:4px;">
              <i class="el-icon-unlock" style="margin-right:3px;font-weight:bold;"></i>已特批开放试用
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="salesman" label="业务员" min-width="110" align="center">
          <template slot-scope="s">{{ s.row.salesman || '-' }}</template>
        </el-table-column>
        <el-table-column label="消费情况" width="100" align="center">
          <template slot-scope="s">
            <el-tag size="small" :type="s.row.consumptionStatus === 1 ? 'success' : 'info'" effect="plain">
              {{ s.row.consumptionStatus === 1 ? '已消费' : '未消费' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="消费时间" min-width="120" align="center">
          <template slot-scope="s">
            <span v-if="s.row.consumptionStatus === 1 && s.row.consumptionTime" style="color: #606266; font-size: 13px;">
              {{ formatDate(s.row.consumptionTime, 10) }}
            </span>
            <span v-else style="color: #909399;">-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="230" align="center">
          <template slot-scope="s">
            <el-button
              v-if="$hasPerm('func:trial:consumption') || isSuperAdmin"
              size="small"
              :type="s.row.consumptionStatus === 1 ? 'info' : 'success'"
              plain
              @click="toggleConsumption(s.row)"
            >
              {{ s.row.consumptionStatus === 1 ? '设为未消费' : '标记已消费' }}
            </el-button>
            <template v-if="($hasPerm('func:trial:unlock-limit') || isSuperAdmin) && s.row.productId && s.row.limitExceeded">
              <el-button
                v-if="s.row.isUnlocked"
                size="small"
                type="danger"
                plain
                icon="el-icon-lock"
                style="margin-left: 8px;"
                @click="lockTrialLimit(s.row)"
              >
                恢复限制
              </el-button>
              <el-button
                v-else
                size="small"
                type="warning"
                plain
                icon="el-icon-unlock"
                style="margin-left: 8px;"
                @click="unlockTrialLimit(s.row)"
              >
                解除限制
              </el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!trialRecords.length" class="muted" style="padding:12px;text-align:center;">暂无产品试用记录</div>
    </el-card>

    <!-- 提货申请 -->
    <el-card class="mb-4 embed-flat" v-if="customer.id">
      <div slot="header" class="card-head">
        <span><i class="el-icon-document"></i> 提货申请</span>
      </div>
      <vip-order :embedded-customer="customer"></vip-order>
    </el-card>

    <!-- 审批管理 -->
    <el-card ref="anchorApproval" class="mb-4 embed-flat" v-if="customer.id">
      <div slot="header" class="card-head">
        <span><i class="el-icon-s-check"></i> 审批管理</span>
      </div>
      <approval-management :embedded-customer="customer"></approval-management>
    </el-card>

    <!-- 出库管理 -->
    <el-card ref="anchorOutbound" class="mb-4 embed-flat" v-if="customer.id">
      <div slot="header" class="card-head">
        <span><i class="el-icon-truck"></i> 出库管理</span>
      </div>
      <outbound-management :embedded-customer="customer"></outbound-management>
    </el-card>

    <!-- 基础信息编辑弹窗（同 Customer.vue 列表的编辑弹窗） -->
    <el-dialog title="编辑客户" :visible.sync="dialogVisible" width="880px" top="6vh" custom-class="customer-dialog" :close-on-click-modal="false" :close-on-press-escape="false">
      <el-form :model="form" :rules="rules" ref="editForm" label-width="110px" size="small">
        <el-row :gutter="24">
          <el-col :span="12"><el-form-item label="客户ID">
            <el-input :value="form.id" disabled></el-input>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="客户姓名" prop="name">
            <el-input v-model="form.name" placeholder="请输入客户姓名"></el-input>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="性别">
            <el-radio-group v-model="form.gender">
              <el-radio label="男">男</el-radio>
              <el-radio label="女">女</el-radio>
            </el-radio-group>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="年龄">
            <el-input-number v-model="form.age" :min="0" :max="150" controls-position="right" style="width: 100%" placeholder="请输入年龄"></el-input-number>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="手机" prop="contactPhone">
            <el-input v-model="form.contactPhone" placeholder="请输入手机号" maxlength="11"></el-input>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="座机" prop="telephone">
            <el-input v-model="form.telephone" placeholder="请输入座机号" maxlength="20"></el-input>
          </el-form-item></el-col>
          <el-col :span="24"><el-form-item label="地址">
            <el-input v-model="form.address" placeholder="请输入详细地址"></el-input>
          </el-form-item></el-col>

          <!-- 开发人 客户负责人 放在地址的下面 -->
          <el-col :span="12"><el-form-item label="开发人" prop="developer">
            <el-input v-model="form.developer" placeholder="请输入开发人"></el-input>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="客户负责人" prop="responsiblePerson">
            <el-select v-model="form.responsiblePerson" filterable clearable placeholder="请选择业务员" style="width: 100%">
              <el-option v-for="u in salesmanList" :key="u.id" :label="u.name || u.username" :value="u.name || u.username"></el-option>
            </el-select>
          </el-form-item></el-col>

          <!-- 客户标签 放在客户类别的前面 他俩同一行 -->
          <el-col :span="12"><el-form-item label="客户标签">
            <el-select v-model="form.customerLabel" placeholder="选择标签" clearable style="width: 100%">
              <el-option v-for="l in labelOptions" :key="l.id" :label="l.name" :value="l.name"></el-option>
            </el-select>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="客户类别">
            <el-select v-model="form.customerCategory" placeholder="请选择类别" clearable style="width:100%">
              <el-option v-for="c in categoryOptions" :key="c.id" :label="c.name" :value="c.name"></el-option>
            </el-select>
          </el-form-item></el-col>

          <el-col :span="12"><el-form-item label="无效客户">
            <el-radio-group v-model="form.invalidCustomer">
              <el-radio :label="0">否</el-radio>
              <el-radio :label="1">是</el-radio>
            </el-radio-group>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="创建日期">
            <el-input :value="formatDate(form.createTime)" disabled></el-input>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="最新修改日期">
            <el-input :value="formatDate(form.updateTime)" disabled></el-input>
          </el-form-item></el-col>
        </el-row>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="success" @click="handleSubmit">保存</el-button>
      </div>
    </el-dialog>

    <!-- 导出勾选 -->
    <el-dialog title="导出客户信息" :visible.sync="exportDialogVisible" width="460px" :close-on-click-modal="false">
      <div style="margin-bottom:10px;color:#909399;font-size:13px;">勾选要导出的模块（每个模块一个 Sheet）：</div>
      <el-checkbox :indeterminate="exportIndeterminate" v-model="exportCheckAll" @change="onExportCheckAll">全选</el-checkbox>
      <el-divider style="margin:10px 0;"></el-divider>
      <el-checkbox-group v-model="exportModules" @change="onExportModulesChange">
        <el-checkbox v-for="m in exportModuleOptions" :key="m.value" :label="m.value" style="display:block;margin:8px 0;">{{ m.label }}</el-checkbox>
      </el-checkbox-group>
      <span slot="footer">
        <el-button @click="exportDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="exporting" :disabled="!exportModules.length" @click="doExport">导出</el-button>
      </span>
    </el-dialog>

    <!-- 标记试用消费弹窗 -->
    <el-dialog
      title="标记试用产品已消费"
      :visible.sync="consumptionDialogVisible"
      width="480px"
      :close-on-click-modal="false"
      append-to-body>
      <el-form :model="consumptionForm" :rules="consumptionRules" ref="consumptionFormRef" label-width="100px" size="small">
        <el-form-item label="客户姓名">
          <span style="font-weight:600;color:#303133;">{{ (customer && customer.name) || '-' }}</span>
        </el-form-item>
        <el-form-item label="试用产品">
          <span style="font-weight:600;color:#409EFF;">{{ currentConsumptionRow ? currentConsumptionRow.productName : '-' }}</span>
        </el-form-item>
        <el-form-item label="试用类型" v-if="currentConsumptionRow && currentConsumptionRow.applicableTypeName">
          <el-tag :color="currentConsumptionRow.applicableTypeColor || ''" :style="currentConsumptionRow.applicableTypeColor ? 'color:#fff;border-color:' + currentConsumptionRow.applicableTypeColor : ''" size="mini">
            {{ currentConsumptionRow.applicableTypeName }}
          </el-tag>
        </el-form-item>
        <el-form-item label="消费时间" prop="consumptionTime">
          <el-date-picker
            v-model="consumptionForm.consumptionTime"
            type="date"
            value-format="yyyy-MM-dd"
            format="yyyy-MM-dd"
            placeholder="请选择消费日期"
            style="width: 100%">
          </el-date-picker>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="consumptionDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="consumptionSubmitting" @click="submitConsumption">确定标记已消费</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { customerApi, userApi } from '@/api/api'
import SalesOrder from './SalesOrder.vue'
import VipOrder from './VipOrder.vue'
import ApprovalManagement from './ApprovalManagement.vue'
import OutboundManagement from './OutboundManagement.vue'

export default {
  name: 'CustomerDetail',
  components: { SalesOrder, VipOrder, ApprovalManagement, OutboundManagement },
  data() {
    return {
      loading: false,
      exporting: false,
      exportDialogVisible: false,
      exportCheckAll: true,
      exportIndeterminate: false,
      exportModuleOptions: [
        { value: 'base', label: '客户基础信息' },
        { value: 'stock', label: '客户实际存货' },
        { value: 'trial', label: '产品试用记录' },
        { value: 'vipcards', label: '尊贵卡剩余可领取' },
        { value: 'order', label: '销售订单（含明细）' },
        { value: 'pickup', label: '提货申请' },
        { value: 'approval', label: '审批管理' },
        { value: 'outbound', label: '出库管理' }
      ],
      exportModules: ['base', 'stock', 'trial', 'vipcards', 'order', 'pickup', 'approval', 'outbound'],
      customer: {},
      managerOutboundTypes: [],
      stockTypeFilter: '',
      stockRows: [],
      trialRecords: [],
      vipCards: [],
      newoutPending: [],
      // 标记试用消费弹窗
      consumptionDialogVisible: false,
      consumptionSubmitting: false,
      currentConsumptionRow: null,
      consumptionForm: {
        consumptionTime: ''
      },
      consumptionRules: {
        consumptionTime: [{ required: true, message: '请选择消费时间', trigger: 'change' }]
      },
      // 基础信息编辑弹窗
      dialogVisible: false,
      form: {},
      labelOptions: [],
      categoryOptions: [],
      salesmanList: [],
      rules: {
        name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        developer: [{ required: true, message: '请输入开发人', trigger: 'blur' }],
        responsiblePerson: [{ required: true, message: '请选择客户负责人', trigger: 'change' }],
        contactPhone: [
          { validator: (rule, value, cb) => {
              const mobile = (value || '').trim()
              const tel = (this.form.telephone || '').trim()
              if (!mobile && !tel) return cb(new Error('手机号和座机号至少填写一项'))
              if (mobile && !/^1[3-9]\d{9}$/.test(mobile)) return cb(new Error('请输入正确的 11 位手机号'))
              cb()
            }, trigger: 'blur' }
        ],
        telephone: [
          { validator: (rule, value, cb) => {
              const tel = (value || '').trim()
              const mobile = (this.form.contactPhone || '').trim()
              if (!mobile && !tel) return cb(new Error('手机号和座机号至少填写一项'))
              if (tel && !/^[\d\-]{6,20}$/.test(tel)) return cb(new Error('请输入正确的座机号（数字或含连字符，6~20 位）'))
              cb()
            }, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    /** 判断是否为超级管理员 */
    isSuperAdmin() {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}')
      return u && (u.role === 'super_admin' || u.roleCode === 'super_admin' || Number(u.roleId) === 1)
    },
    /** 新出库未完结提醒：单行汇总「产品 申请日, 产品 申请日, ...」，不含单号 */
    newoutPendingLine() {
      return (this.newoutPending || []).map(r => {
        const pn = r.productNames || '-'
        const dt = (r.createTime || '').toString().slice(0, 10) || '-'
        return `${pn}（申请日 ${dt}）`
      }).join('，')
    },
    filteredStockRows() {
      if (!this.stockTypeFilter) return this.stockRows || []
      return (this.stockRows || []).filter(r => (r.stockType || '普通存货') === this.stockTypeFilter)
    }
  },
  created() {
    const id = this.$route.params.id
    if (!id) return
    this.loadDetail(id)
    this.loadStockSummary(id)
    this.loadTrialRecords(id)
    this.loadVipCardsSummary(id)
    this.loadNewoutPending(id)
    this.loadLabels()
    this.loadCategories()
    this.loadSalesmen()
  },
  watch: {
    // 客户加载完成后才有 v-if 渲染出审批/出库 card；再分多次滚动，等子组件加载完页面高度稳定
    'customer.id'(v) { if (v) this.scheduleScrollToAnchor() },
    '$route.query.anchor'() { this.scheduleScrollToAnchor() }
  },
  methods: {
    maskPhone(v) {
      const s = (v == null ? '' : String(v)).trim()
      if (!s || s === '00000000000' || /^0+$/.test(s)) return '-'
      const me = JSON.parse(sessionStorage.getItem('user') || '{}')
      const isAdmin = me && me.role === 'admin' && Number(me.roleId) !== 1
      if (!isAdmin) return s
      if (s.length === 11) return s.slice(0, 3) + '****' + s.slice(7)
      if (s.length >= 7) return s.slice(0, 3) + '****' + s.slice(-2)
      return s.slice(0, 2) + '***'
    },
    openExportDialog() {
      const id = this.customer && this.customer.id
      if (!id) { this.$message.warning('客户信息未加载完成，请稍候'); return }
      // 默认全选
      this.exportModules = this.exportModuleOptions.map(m => m.value)
      this.exportCheckAll = true
      this.exportIndeterminate = false
      this.exportDialogVisible = true
    },
    onExportCheckAll(val) {
      this.exportModules = val ? this.exportModuleOptions.map(m => m.value) : []
      this.exportIndeterminate = false
    },
    onExportModulesChange(val) {
      const n = val.length, total = this.exportModuleOptions.length
      this.exportCheckAll = n === total
      this.exportIndeterminate = n > 0 && n < total
    },
    /** 按勾选的模块导出该客户信息（每模块一个 Sheet） */
    doExport() {
      const id = this.customer && this.customer.id
      if (!id || !this.exportModules.length) return
      this.exporting = true
      axios.get('/export/customer-full', { params: { id, modules: this.exportModules.join(',') }, responseType: 'blob' }).then(res => {
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = '客户信息_' + (this.customer.name || id) + '_' + new Date().toISOString().slice(0, 10) + '.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
        this.exportDialogVisible = false
      }).catch(() => { this.$message.error('导出失败，请重试') }).finally(() => { this.exporting = false })
    },
    /** 客户详情 + 子组件都是异步加载，分多个时间点反复滚动以校正最终位置 */
    scheduleScrollToAnchor() {
      const anchor = this.$route && this.$route.query && this.$route.query.anchor
      if (!anchor) return
      const refName = anchor === 'outbound' ? 'anchorOutbound' : (anchor === 'approval' ? 'anchorApproval' : null)
      if (!refName) return
      const doScroll = () => {
        const c = this.$refs[refName]
        const el = c && c.$el ? c.$el : null
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      // 子组件 OutboundManagement / ApprovalManagement 拉取数据 + 渲染会撑开页面高度，
      // 早期 scrollIntoView 滚的位置后面会偏；分时间点反复校正最终位置
      ;[100, 500, 1000, 1800, 2800].forEach(ms => setTimeout(doScroll, ms))
    },
    loadDetail(id) {
      this.loading = true
      customerApi.getCustomerById(id)
        .then(res => {
          this.customer = res.data || {}
          this.loadManagerOutboundTypes()
        })
        .finally(() => { this.loading = false })
    },
    loadManagerOutboundTypes() {
      const name = this.customer && this.customer.responsiblePerson
      if (!name) { this.managerOutboundTypes = []; return }
      axios.get('/user/outbound-types-by-name', { params: { name } })
        .then(res => { this.managerOutboundTypes = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.managerOutboundTypes = [] })
    },
    loadStockSummary(id) {
      axios.get(`/customer/${id}/stock-summary`).then(res => {
        this.stockRows = (res.data && res.data.rows) || []
      }).catch(() => { this.stockRows = [] })
    },
    loadTrialRecords(id) {
      const custId = id || (this.customer && this.customer.id)
      if (!custId) return
      axios.get('/trial/customer-records', { params: { customerId: custId } }).then(res => {
        this.trialRecords = (res.data && res.data.records) || []
      }).catch(() => { this.trialRecords = [] })
    },
    toggleConsumption(row) {
      if (row.consumptionStatus === 1) {
        const custName = (this.customer && this.customer.name) || ''
        const prodName = row.productName || '该产品'

        this.$confirm(
          `确认将客户【${custName}】针对产品【${prodName}】的试用状态设为【未消费】吗？`,
          '设为未消费确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        ).then(() => {
          axios.put('/trial/toggle-consumption', {
            customerId: this.customer && this.customer.id,
            productId: row.productId,
            applicableTypeId: row.applicableTypeId,
            applicationId: row.applicationId,
            applicationIds: row.applicationIds,
            consumptionStatus: 0
          }).then(res => {
            if (res.data && res.data.success) {
              this.$message.success(res.data.message || '已设为【未消费】')
              row.consumptionStatus = 0
              row.consumptionTime = null
              this.loadTrialRecords()
            } else {
              this.$message.error((res.data && res.data.message) || '操作失败')
            }
          }).catch(err => {
            this.$message.error('操作失败：' + ((err.response && err.response.data && err.response.data.message) || err.message))
          })
        }).catch(() => {})
      } else {
        this.currentConsumptionRow = row
        const now = new Date()
        const yyyy = now.getFullYear()
        const mm = String(now.getMonth() + 1).padStart(2, '0')
        const dd = String(now.getDate()).padStart(2, '0')
        const defaultDate = `${yyyy}-${mm}-${dd}`

        this.consumptionForm = {
          consumptionTime: defaultDate
        }
        this.consumptionDialogVisible = true
        this.$nextTick(() => {
          if (this.$refs.consumptionFormRef) {
            this.$refs.consumptionFormRef.clearValidate()
          }
        })
      }
    },
    submitConsumption() {
      this.$refs.consumptionFormRef.validate(valid => {
        if (!valid) return
        const row = this.currentConsumptionRow
        if (!row) return
        this.consumptionSubmitting = true
        axios.put('/trial/toggle-consumption', {
          customerId: this.customer && this.customer.id,
          productId: row.productId,
          applicableTypeId: row.applicableTypeId,
          applicationId: row.applicationId,
          applicationIds: row.applicationIds,
          consumptionStatus: 1,
          consumptionTime: this.consumptionForm.consumptionTime
        }).then(res => {
          this.consumptionSubmitting = false
          if (res.data && res.data.success) {
            this.$message.success(res.data.message || '已标记为【已消费】')
            this.consumptionDialogVisible = false
            row.consumptionStatus = 1
            row.consumptionTime = this.consumptionForm.consumptionTime
            this.loadTrialRecords()
          } else {
            this.$message.error((res.data && res.data.message) || '操作失败')
          }
        }).catch(err => {
          this.consumptionSubmitting = false
          this.$message.error('操作失败：' + ((err.response && err.response.data && err.response.data.message) || err.message))
        })
      })
    },
    unlockTrialLimit(row) {
      const custName = (this.customer && this.customer.name) || '当前客户'
      const prodName = row.productName || '该产品'
      const applyCount = row.totalApplyCount !== undefined ? row.totalApplyCount : ((row.approvedCount || 0) + (row.pendingCount || 0))
      const maxAllowed = row.maxAllowed || row.applicableCount || '-'
      const approvedCount = row.approvedCount || 0
      const pendingCount = row.pendingCount || 0

      const htmlMsg = `
        <div style="font-size: 14px; line-height: 1.8; color: #606266;">
          <p style="margin: 0 0 10px 0; font-size: 15px; font-weight: bold; color: #E6A23C;">
            <i class="el-icon-warning" style="font-size: 16px; margin-right: 4px;"></i>
            确定要为该客户解除试用上限限制吗？
          </p>
          <div style="background: #FAFAFA; border: 1px solid #EBEEF5; border-radius: 4px; padding: 10px 14px; margin-bottom: 12px;">
            <div style="margin-bottom: 4px;">客户姓名：<strong style="color: #303133;">${custName}</strong></div>
            <div style="margin-bottom: 4px;">试用产品：<strong style="color: #409EFF;">${prodName}</strong></div>
            <div style="margin-bottom: 4px;">试用上限：<strong style="color: #F56C6C;">${maxAllowed} 次</strong></div>
            <div>当前申请：已申请 <strong style="color: #409EFF;">${applyCount} 次</strong>（已审核 <span style="color:#67C23A;font-weight:600;">${approvedCount} 次</span> · 待审核 <span style="color:#E6A23C;font-weight:600;">${pendingCount} 次</span>）</div>
          </div>
          <div style="background: #FDF6EC; border-left: 3px solid #E6A23C; padding: 8px 12px; border-radius: 2px; color: #E6A23C; font-size: 12px; line-height: 1.6;">
            <strong>特别提示：</strong><br/>
            1. 解除限制后，客户可<strong>再申请 1 次</strong>该产品的试用；<br/>
            2. 业务员提交该笔提货申请后，系统会自动消耗此次特批机会并重新校验上限。
          </div>
        </div>
      `

      this.$confirm(htmlMsg, '解除试用限制确认', {
        dangerouslyUseHTMLString: true,
        confirmButtonText: '确定解除限制',
        cancelButtonText: '取消',
        confirmButtonClass: 'el-button--warning',
        type: 'warning',
        closeOnClickModal: false,
        closeOnPressEscape: true
      }).then(() => {
        axios.post('/trial/unlock-limit', {
          customerId: this.customer.id,
          productId: row.productId,
          remark: '管理员在客户详情页手动解除试用上限'
        }).then(res => {
          if (res.data && res.data.success) {
            this.$message.success(res.data.message || '已成功解除限制，客户可再申请1次试用')
            this.loadTrialRecords()
          } else {
            this.$message.error((res.data && res.data.message) || '操作失败')
          }
        }).catch(err => {
          this.$message.error('操作失败：' + (err.message || '网络错误'))
        })
      }).catch(() => {})
    },
    lockTrialLimit(row) {
      const custName = (this.customer && this.customer.name) || '当前客户'
      const prodName = row.productName || '该产品'
      const applyCount = row.totalApplyCount !== undefined ? row.totalApplyCount : ((row.approvedCount || 0) + (row.pendingCount || 0))
      const maxAllowed = row.maxAllowed || row.applicableCount || '-'
      const approvedCount = row.approvedCount || 0
      const pendingCount = row.pendingCount || 0

      const htmlMsg = `
        <div style="font-size: 14px; line-height: 1.8; color: #606266;">
          <p style="margin: 0 0 10px 0; font-size: 15px; font-weight: bold; color: #F56C6C;">
            <i class="el-icon-warning" style="font-size: 16px; margin-right: 4px;"></i>
            确定要为该客户重新恢复试用上限限制吗？
          </p>
          <div style="background: #FAFAFA; border: 1px solid #EBEEF5; border-radius: 4px; padding: 10px 14px; margin-bottom: 12px;">
            <div style="margin-bottom: 4px;">客户姓名：<strong style="color: #303133;">${custName}</strong></div>
            <div style="margin-bottom: 4px;">受限产品：<strong style="color: #409EFF;">${prodName}</strong></div>
            <div style="margin-bottom: 4px;">试用上限：<strong style="color: #F56C6C;">${maxAllowed} 次</strong></div>
            <div>当前申请：已申请 <strong style="color: #409EFF;">${applyCount} 次</strong>（已审核 <span style="color:#67C23A;font-weight:600;">${approvedCount} 次</span> · 待审核 <span style="color:#E6A23C;font-weight:600;">${pendingCount} 次</span>）</div>
          </div>
          <div style="background: #FEF0F0; border-left: 3px solid #F56C6C; padding: 8px 12px; border-radius: 2px; color: #F56C6C; font-size: 12px; line-height: 1.6;">
            <strong>特别提示：</strong><br/>
            恢复限制后，由于该客户对该产品的试用申请已达到上限，将<strong>无法继续提交</strong>该产品的试用申请，直到超级管理员再次解除限制。
          </div>
        </div>
      `

      this.$confirm(htmlMsg, '恢复试用限制确认', {
        dangerouslyUseHTMLString: true,
        confirmButtonText: '确定恢复限制',
        cancelButtonText: '取消',
        confirmButtonClass: 'el-button--danger',
        type: 'warning',
        closeOnClickModal: false,
        closeOnPressEscape: true
      }).then(() => {
        axios.post('/trial/lock-limit', {
          customerId: this.customer.id,
          productId: row.productId,
          remark: '管理员在客户详情页手动恢复试用限制'
        }).then(res => {
          if (res.data && res.data.success) {
            this.$message.success(res.data.message || '已成功恢复限制')
            this.loadTrialRecords()
          } else {
            this.$message.error((res.data && res.data.message) || '操作失败')
          }
        }).catch(err => {
          this.$message.error('操作失败：' + (err.message || '网络错误'))
        })
      }).catch(() => {})
    },
    isImageFile(url) {
      if (!url) return false
      return /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url)
    },
    full(url) {
      if (!url) return ''
      if (/^(https?:|data:)/i.test(url)) return url
      const base = (axios.defaults.baseURL || '').replace(/\/api\/?$/, '')
      return base + url
    },
    downloadAttachment(url) {
      if (!url) return
      const downloadApiUrl = '/api/file/download?url=' + encodeURIComponent(url)
      window.open(downloadApiUrl, '_blank')
    },
    exportTrialCsv() {
      if (!this.trialRecords.length) return
      const headers = ['序号', '产品名称', '总申请件数', '已审核件数', '待审核件数', '试用类型', '已申请总次数', '已审核次数', '待审核次数', '上限限制', '消费情况', '消费时间', '业务员', '申请时间', '审核时间', '核验方式', '核验手机号']
      const rows = this.trialRecords.map((r, i) => [
        this.trialRecords.length - i,
        r.productName || '-',
        r.totalQuantity !== undefined ? r.totalQuantity : (r.quantity || 1),
        r.approvedQuantity !== undefined ? r.approvedQuantity : r.quantity,
        r.pendingQuantity || 0,
        r.applicableTypeName || '-',
        r.totalApplyCount !== undefined ? r.totalApplyCount : ((r.approvedCount || 0) + (r.pendingCount || 0)),
        r.approvedCount || 0,
        r.pendingCount || 0,
        r.applicableCount ? r.applicableCount + '次' : '不限',
        r.consumptionStatus === 1 ? '已消费' : '未消费',
        r.consumptionStatus === 1 && r.consumptionTime ? this.formatDate(r.consumptionTime, 10) : '-',
        r.salesman || '-',
        this.formatDate(r.applyTime),
        this.formatDate(r.approveTime),
        r.verificationType === 1 ? '短信验证' : (r.verificationType === 2 ? '特殊通道附件' : '免核验'),
        r.verifyPhone || '-'
      ])
      let csvContent = '\uFEFF' + headers.join(',') + '\n'
      rows.forEach(row => {
        const line = row.map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(',')
        csvContent += line + '\n'
      })
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `客户产品试用记录_${this.customer.name || ''}_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },
    loadVipCardsSummary(id) {
      // 直接复用销售订单页数据（/order/page），不再单独重算，保证与销售订单展开里的剩余额度/剩余可领取完全一致
      const catOrder = { '尊贵卡A': 0, '尊贵卡B': 1, '尊贵卡C': 2 }
      axios.get('/order/page', { params: { customerId: id, vipMode: true, page: 1, size: 100 } }).then(res => {
        const records = (res.data && res.data.records) || []
        this.vipCards = records
          .filter(o => catOrder[o.category] != null)
          .sort((a, b) => (catOrder[a.category] - catOrder[b.category]) || ((a.cardSeq || 1) - (b.cardSeq || 1)))
          .map(o => {
            const seq = o.cardSeq != null ? o.cardSeq : 1
            const card = { cardName: o.category, cardSeq: seq, label: o.category + seq }
            if (o.category === '尊贵卡B') {
              card.mode = 'amount'
              card.remainingAmount = o.vipBRemaining != null ? o.vipBRemaining : 0
              card.shouldQuotaRemaining = o.vipBRemaining != null ? o.vipBRemaining : (o.shouldQuotaRemaining != null ? o.shouldQuotaRemaining : (o.totalAmount || 0))
              card.actualQuotaRemaining = o.actualQuotaRemaining != null ? o.actualQuotaRemaining : 0
            } else {
              card.mode = 'qty'
              // 已变更作废明细（voided===1）不在「尊贵卡剩余可领取」展示
              card.products = (o.details || []).filter(d => d.productId != null && Number(d.voided) !== 1).map(d => {
                let pname = d.productName || ''
                if (pname && pname.indexOf('尊贵卡-') !== 0) pname = '尊贵卡-' + pname
                return {
                  productName: pname,
                  remaining: d.remainingPickup != null ? d.remainingPickup : (d.quantity || 0),
                  shouldRemaining: d.shouldRemaining != null ? d.shouldRemaining : (d.remainingPickup != null ? d.remainingPickup : (d.stockLimit || d.quantity || 0)),
                  actualRemaining: d.actualRemaining != null ? d.actualRemaining : 0
                }
              })
            }
            return card
          })
      }).catch(() => { this.vipCards = [] })
    },
    /** 按 [feedback_type_color_scheme]：A→金/warning、B→玫红/danger、C→紫/info */
    vipTagType(cardName) {
      if (cardName === '尊贵卡A') return 'warning'
      if (cardName === '尊贵卡B') return 'danger'
      return 'info'
    },
    /** C 用自定义紫色 tag class（element 没紫色 type）；A/B 也走自定义 class 统一调色 */
    vipTagClass(cardName) {
      if (cardName === '尊贵卡A') return 'tag-vip-a'
      if (cardName === '尊贵卡B') return 'tag-vip-b'
      if (cardName === '尊贵卡C') return 'tag-vip-c'
      return ''
    },
    formatMoney(v) {
      if (v == null || v === '') return '0'
      const n = Number(v)
      if (isNaN(n)) return v
      // 保留 2 位小数；整数则去掉小数
      return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '')
    },
    /** 新出库未完结 = outboundType=新出库 且 status=4（未结清，待付款） */
    loadNewoutPending(id) {
      axios.get('/outbound/page', { params: { customerId: id, outboundType: '新出库', status: 4, page: 1, size: 100 } })
        .then(res => {
          const records = (res.data && res.data.records) || []
          this.newoutPending = records.filter(r => r.status !== 6)
        })
        .catch(() => { this.newoutPending = [] })
    },
    openEdit() {
      if (!this.customer || !this.customer.id) return
      // 复制一份 customer 到 form 作为编辑缓冲
      this.form = JSON.parse(JSON.stringify(this.customer))
      this.dialogVisible = true
      this.$nextTick(() => { if (this.$refs.editForm) this.$refs.editForm.clearValidate() })
    },
    handleSubmit() {
      this.$refs.editForm.validate((valid) => {
        if (!valid) return
        // phone 是数据库 NOT NULL：手机号优先，座机号兜底（与 Customer.vue 一致）
        if (this.form.contactPhone) this.form.phone = this.form.contactPhone
        else if (this.form.telephone) this.form.phone = this.form.telephone
        customerApi.updateCustomer(this.form).then(res => {
          if (res.data) {
            this.$message.success('更新成功')
            this.dialogVisible = false
            this.loadDetail(this.customer.id)
            this.loadStockSummary(this.customer.id)
          } else {
            this.$message.error('操作失败')
          }
        })
      })
    },
    loadLabels() {
      axios.get('/customer-label/list').then(res => {
        const list = Array.isArray(res.data) ? res.data : []
        this.labelOptions = list.filter(l => l.enabled !== 0)
      }).catch(() => {})
    },
    loadCategories() {
      axios.get('/customer-category/list').then(res => {
        const list = Array.isArray(res.data) ? res.data : []
        this.categoryOptions = list.filter(c => c.enabled !== 0)
      }).catch(() => {})
    },
    loadSalesmen() {
      userApi.getUserList().then(res => {
        const list = Array.isArray(res.data) ? res.data : []
        this.salesmanList = list.filter(u => (u.role === 'salesman' || u.roleId === 3) && u.status !== 0)
      }).catch(() => { this.salesmanList = [] })
    },
    formatDate(v, len) {
      if (!v) return '-'
      return String(v).replace('T', ' ').slice(0, len || 19)
    },
    labelType(label) {
      if (label === '休眠') return 'warning'
      if (!label || label === '正常') return 'success'
      return 'info'
    }
  }
}
</script>

<style scoped>
.container { padding: 15px; }
.mb-4 { margin-bottom: 15px; }
/* 新出库未完结提醒 */
.newout-pending-alert ::v-deep .el-alert__title { font-size: 14px; font-weight: 600; color: #E6A23C; }
.newout-pending-alert ::v-deep .el-alert__content { padding-right: 16px; }
.newout-pending-alert .alert-list { margin-top: 6px; font-size: 13px; color: #606266; }
.newout-pending-alert .alert-row { padding: 2px 0; }
.newout-pending-alert .alert-sep { margin: 0 6px; color: #c0c4cc; }
.newout-pending-alert .alert-no { font-weight: 600; color: #303133; }
.newout-pending-alert .alert-debt { color: #f56c6c; font-weight: 600; }
.newout-pending-alert .alert-tip { margin-top: 8px; padding-top: 6px; border-top: 1px dashed #f5dab1; font-size: 12px; color: #909399; }
.card-head {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 15px; font-weight: 600;
}
.card-head i { margin-right: 6px; color: #1976D2; }
.muted { color: #909399; }
/* 尊贵卡剩余可领取：多产品同行换行，每行间距 */
.vip-row-cell { line-height: 22px; padding: 2px 0; }
.vip-row-cell + .vip-row-cell { border-top: 1px dashed #ebeef5; }
/* 尊贵卡 A/B/C 卡片编号 tag 自定义色（与 ApprovalManagement / VipOrder 同款） */
::v-deep .el-tag.tag-vip-a {
  background-color: #fff1b8 !important;
  border-color: #faad14 !important;
  color: #874d00 !important;
  font-weight: 600 !important;
}
::v-deep .el-tag.tag-vip-b {
  background-color: #fff0f6 !important;
  border-color: #ff85c0 !important;
  color: #c41d7f !important;
  font-weight: 600 !important;
}
::v-deep .el-tag.tag-vip-c {
  background-color: #f4ecfb !important;
  border-color: #d3b4f1 !important;
  color: #722ed1 !important;
  font-weight: 600 !important;
}
/* 基础信息字号 +2（含标签内文字） */
.base-info-desc >>> .el-descriptions-item__label,
.base-info-desc >>> .el-descriptions-item__content,
.base-info-desc >>> .el-tag { font-size: 15px !important; }
/* 两列等宽：固定表格布局；超长内容（如客户负责人后的出库权限标注）在格内换行而不撑列宽 */
.base-info-desc >>> table { table-layout: fixed; width: 100%; }
.base-info-desc >>> .el-descriptions-item__cell { width: auto; }
.base-info-desc >>> .el-descriptions-item__content { word-break: break-all; white-space: normal; }
/* 销售订单/提货申请/审批管理/出库管理：外层 el-card 正常显示（与"客户实际存货"同款），
   内层子组件根 el-card 扁平化（透明无 padding 无边框无阴影），最终视觉上只剩一层卡片 */
.embed-flat >>> .el-card { background: transparent !important; border: none !important; box-shadow: none !important; }
.embed-flat >>> .el-card__body { padding: 10px !important; background: transparent !important; overflow-x: auto !important; }
</style>
